import { useState } from 'react';
import { Button } from '@/components/ui/button';

interface ConsentDisclosureProps {
  envelopeTitle: string;
  ownerName: string;
  disclosureText: string;
  onAccept: () => void | Promise<void>;
  onDecline: () => void;
  pending?: boolean;
}

/**
 * Pre-signing consent step. The text is the legally relevant disclosure for
 * US ESIGN/UETA compliance — the recipient must see it AND click an
 * affirmative checkbox before any field becomes interactive. The accept
 * action also captures the recipient's IP + UA on the server.
 */
export function ConsentDisclosure({
  envelopeTitle,
  ownerName,
  disclosureText,
  onAccept,
  onDecline,
  pending,
}: ConsentDisclosureProps) {
  const [checked, setChecked] = useState(false);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Review and sign "{envelopeTitle}"</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {ownerName} has requested your signature. Before continuing, please review the disclosure
          below and confirm your consent to sign electronically.
        </p>
      </header>
      <section className="border rounded-md p-4 bg-muted/40">
        <h2 className="text-sm font-semibold mb-2">Electronic signature disclosure</h2>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{disclosureText}</p>
      </section>
      <label className="flex items-start gap-2 text-sm cursor-pointer select-none">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => setChecked(e.target.checked)}
          className="mt-1"
        />
        <span>
          I have read the disclosure above and agree to use electronic records and signatures.
        </span>
      </label>
      <div className="flex flex-wrap gap-2 pt-2">
        <Button onClick={onAccept} disabled={!checked || pending}>
          {pending ? 'Continuing…' : 'I agree, continue to sign'}
        </Button>
        <Button variant="outline" onClick={onDecline} disabled={pending}>
          Decline
        </Button>
      </div>
    </div>
  );
}
